import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../../config';
import { getBearerAuthHeaders } from '../../utils/authFetchHeaders';
import { fmtPct, sentimentClass } from './clientReportingFormat';
import { HoverTip, useHoverTip } from './clientReportingHoverTip';
import { MarketIcon } from './clientReportingMarketIcons';
import ClientReportingQuoteTip from './ClientReportingQuoteTip';
import { formatQuotePrice, isPointsQuoted } from './clientReportingQuoteDetails';
import { quotesBySymbolFromResponse } from './clientReportingSidebarQuotes';
import { WORLD_MARKET_REGIONS } from './clientReportingWorldMarketsConfig';
import { useVisibilityAwareQuotePoll } from './useClientReportingSidebarQuotes';

function formatSignedAmount(value, prefix = '$') {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  const num = Number(value);
  const abs = Math.abs(num).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
  const sign = num < 0 ? '-' : '+';
  return `${sign}${prefix}${abs}`;
}

function formatMarketChange(market) {
  const pct = Number(market?.percentChange);
  const net = Number(market?.netChange);
  if (!Number.isFinite(pct)) return { pctLabel: '—', netLabel: '—', tone: '' };
  return {
    pctLabel: fmtPct(pct),
    netLabel: Number.isFinite(net)
      ? formatSignedAmount(net, market.currencyPrefix || '$')
      : '—',
    tone: sentimentClass(pct),
  };
}

export default function ClientReportingWorldMarkets({
  quotesBySymbol: quotesBySymbolProp,
  loading: loadingProp,
  warning: warningProp,
  regionId: regionIdProp,
  onRegionChange,
}) {
  const controlled = quotesBySymbolProp !== undefined;
  const [internalRegionId, setInternalRegionId] = useState('americas');
  const regionId = regionIdProp ?? internalRegionId;
  const setRegionId = onRegionChange ?? setInternalRegionId;
  const [quotesBySymbol, setQuotesBySymbol] = useState({});
  const [loading, setLoading] = useState(!controlled);
  const [warning, setWarning] = useState('');

  const region = useMemo(
    () => WORLD_MARKET_REGIONS.find((r) => r.id === regionId) || WORLD_MARKET_REGIONS[0],
    [regionId],
  );

  const loadQuotes = useCallback(async () => {
    if (controlled) return;
    setLoading(true);
    setWarning('');
    try {
      const res = await fetch(
        API_ENDPOINTS.marketQuotes({ symbols: region.symbols.join(',') }),
        { headers: getBearerAuthHeaders() },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to load market quotes');
      setQuotesBySymbol((prev) => ({
        ...prev,
        ...quotesBySymbolFromResponse(data.markets),
      }));
      if (data.warning) setWarning(data.warning);
    } catch (err) {
      console.error('[client-reporting-markets]', err);
      setWarning(err.message || 'Market data unavailable.');
    } finally {
      setLoading(false);
    }
  }, [controlled, region]);

  useEffect(() => {
    if (controlled) return undefined;
    loadQuotes();
  }, [controlled, regionId, loadQuotes]);

  useVisibilityAwareQuotePoll(loadQuotes, !controlled, { immediate: false });

  const resolvedQuotes = controlled ? quotesBySymbolProp : quotesBySymbol;
  const resolvedLoading = controlled ? Boolean(loadingProp) : loading;
  const resolvedWarning = controlled ? warningProp : warning;

  const tip = useHoverTip({ width: 268, height: 250 });
  const navigate = useNavigate();

  const rows = region.symbols
    .map((symbol) => {
      const quote = resolvedQuotes[symbol];
      if (!quote) return null;
      const change = formatMarketChange(quote);
      const points = isPointsQuoted(quote.kind);
      return {
        symbol,
        shortLabel: region.labels[symbol] || quote.shortLabel || symbol,
        name: quote.label || symbol,
        change,
        quote,
        points,
        priceLabel: formatQuotePrice(quote.lastPrice ?? quote.close, {
          prefix: quote.currencyPrefix || '$',
          points,
        }),
      };
    })
    .filter(Boolean);

  const activeRow = rows.find((row) => row.symbol === tip.activeKey);

  return (
    <section className="vd-panel vd-ml-widget vd-ml-world-markets">
      <div className="vd-ml-widget-header">
        <h3>World Markets</h3>
      </div>
      {/* Index/futures moves are in points, not dollars — and nothing here is
          related to the client's account. */}
      <p className="vd-ml-widget-caption">
        Index, futures and ETF levels, delayed. The change is today&apos;s move
        since the previous close — index points for futures and indexes, dollars
        for ETFs. Not related to your account.
        {' '}
        <strong>Hover a row for the full quote.</strong>
      </p>

      <div className="vd-ml-region-tabs" role="tablist" aria-label="Market regions">
        {WORLD_MARKET_REGIONS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={regionId === tab.id}
            className={`vd-ml-region-tab${regionId === tab.id ? ' active' : ''}`}
            onClick={() => setRegionId(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className={`vd-ml-market-list${resolvedLoading ? ' vd-ml-widget-loading' : ''}`}>
        {rows.length ? rows.map((row) => (
          /* A button so the full quote is reachable by keyboard, not by hover
             alone — see the matching note in ClientReportingStocks. */
          <button
            type="button"
            className={`vd-ml-market-row vd-ml-market-row-btn${tip.activeKey === row.symbol ? ' active' : ''}`}
            key={row.symbol}
            {...tip.hoverProps(row.symbol)}
            onClick={() => navigate(`/symbol/${row.symbol}`)}
            aria-label={`${row.name} (${row.shortLabel}). `
              + `${row.points ? 'Level' : 'Price'} ${row.priceLabel}. `
              + `Change since previous close ${row.change.netLabel}, ${row.change.pctLabel}. `
              + 'Opens the symbol page.'}
          >
            <MarketIcon symbol={row.symbol} shortLabel={row.shortLabel} />
            <div className="vd-ml-market-copy">
              <div className="vd-ml-market-name">{row.name}</div>
              <div className="vd-ml-market-symbol">{row.shortLabel}</div>
            </div>
            <div className="vd-ml-market-change">
              <span className="vd-ml-market-price">{row.priceLabel}</span>
              <span className="vd-ml-market-delta">
                <span className={`vd-ml-market-pill ${row.change.tone}`}>{row.change.pctLabel}</span>
                <span className={`vd-ml-market-net ${row.change.tone}`}>{row.change.netLabel}</span>
              </span>
            </div>
          </button>
        )) : (
          <p className="vd-ml-widget-empty">
            {resolvedLoading ? 'Loading market quotes…' : (resolvedWarning || 'No market quotes available.')}
          </p>
        )}
      </div>

      <HoverTip position={tip.position} width={268} className="vd-ml-quote-tip">
        {activeRow ? (
          <ClientReportingQuoteTip
            title={activeRow.name}
            subtitle={activeRow.shortLabel}
            quote={activeRow.quote}
            kind={activeRow.quote.kind}
            currencyPrefix={activeRow.quote.currencyPrefix}
          />
        ) : null}
      </HoverTip>
    </section>
  );
}
